/**
 * React hook for using analytics.
 */

import { useCallback, useContext } from 'react';
import { AnalyticsContext } from '../context.jsx';
import type { AnalyticsEvent } from '../types/events.js';
import type { EventName } from '../constants/event-names.js';
import type { EventCategory } from '../constants/event-categories.js';
import type { EventProperties } from '../types/properties.js';

/**
 * Hook for using analytics in React components.
 *
 * @returns Object with track, identify, and reset functions
 */
export function useAnalytics() {
  const { track, identify, reset, isInitialized } = useContext(AnalyticsContext);

  /**
   * Track an analytics event.
   *
   * @param name The event name
   * @param category The event category
   * @param properties Optional event properties
   */
  const trackEvent = useCallback(
    (name: EventName, category: EventCategory, properties?: EventProperties) => {
      track({
        name,
        category,
        properties,
        timestamp: Date.now()
      } as AnalyticsEvent);
    },
    [track]
  );

  return {
    /**
     * Track an analytics event.
     */
    track: trackEvent,

    /**
     * Identify a user.
     */
    identify,

    /**
     * Reset the analytics state.
     */
    reset,

    /**
     * Whether analytics is initialized.
     */
    isInitialized
  };
}
